import React, { useEffect, useState } from "react";
import { Button, useMediaQuery, useTheme } from "@mui/material";
import { MbscCalendarEvent } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { InactivateBookingEntry } from "../../features/Schedule/schedule.types";
import { useRemoteInactivateBookings } from "../../hooks/Remote/Booking/useRemoteInactivateBookings";
import useTimezone from "../../hooks/useTimezone/useTimezone";
import { BookingRequestsStatus } from "../BookingRequest/typings/booking-requests.types";
import { conferenceRoomHasVisitorRegistrations } from "./functions/conference-room-has-visitor-registrations.function";
import { isScheduleTimeLockedByConferenceServiceSetup } from "./functions/isScheduleTimeLockedByConferenceServiceSetup.function";
import { ScheduleCancelConferenceVisitorsDialog } from "./schedule-cancel-conference-visitors-dialog.component";

type P = {
  schedule: BookingScheduleInterface;
  calendarData: MbscCalendarEvent[];
  isEditable: boolean;
  onClose: () => void;
  /** When true, hides cancel booking (computed once at dialog / popover level). */
  isConferenceServiceSetupLock?: boolean;
  onCalendarDataChange?: (events: MbscCalendarEvent[]) => void;
  setDeletingBookings?: (d: boolean) => void;
  handleRejectBookings?: () => void;
  handleApproveBookings?: () => void;
  /** Refetch calendar after room-only cancel so new visit-only tiles appear (Q11b). */
  refetchAllSchedule?: () => void;
};

export const ScheduleEventDeleteButton: React.FC<P> = ({
  schedule,
  calendarData,
  isEditable,
  isConferenceServiceSetupLock = false,
  onClose,
  onCalendarDataChange,
  setDeletingBookings,
  handleRejectBookings,
  handleApproveBookings,
  refetchAllSchedule
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const timezoneState = useSelector((state: RootState) => state.login.settings?.timezone);
  const deviceTimezone = useTimezone();
  const [cancelVisitorsDialogOpen, setCancelVisitorsDialogOpen] = useState(false);
  const [roomOnlyCancelPending, setRoomOnlyCancelPending] = useState(false);

  const { mutate: inactivateBookings, status: inactivateBookingsStatus } =
    useRemoteInactivateBookings();

  const buildInactivatePayload = (cancelVisitorRegistrations?: boolean): InactivateBookingEntry => {
    const entry: InactivateBookingEntry = {
      deleted: false,
      bookingIds: [schedule.id],
      bookingType: schedule.bookingType as BookingType
    };

    if (cancelVisitorRegistrations === true) {
      entry.cancelVisitorRegistrations = true;
    }

    return entry;
  };

  const submitInactivate = (cancelVisitorRegistrations?: boolean) => {
    setDeletingBookings?.(true);
    inactivateBookings({
      bookings: [buildInactivatePayload(cancelVisitorRegistrations)]
    });
  };

  const handleInactiveBookings = () => {
    const timezone = timezoneState === schedule.timezone ? schedule.timezone : deviceTimezone;
    const isCancellationLockedNow = isScheduleTimeLockedByConferenceServiceSetup({
      schedule,
      timezone,
      now: DateTime.now()
    });

    if (isCancellationLockedNow) {
      enqueueSnackbar(t("_schedule_cancelBookingBlockedBySetupTime"), {
        variant: "warning"
      });
      return;
    }

    if (conferenceRoomHasVisitorRegistrations(schedule)) {
      setCancelVisitorsDialogOpen(true);
      return;
    }

    submitInactivate();
  };

  const handleCancelVisitorsDialogClose = () => {
    setCancelVisitorsDialogOpen(false);
  };

  const handleCancelVisitorsDialogConfirm = (cancelVisitorRegistrations: boolean) => {
    setCancelVisitorsDialogOpen(false);
    if (cancelVisitorRegistrations) {
      submitInactivate(true);
      return;
    }
    setRoomOnlyCancelPending(true);
    submitInactivate();
  };

  const handleInactivateSuccess = () => {
    const shouldRefetchAfterRoomOnlyCancel = roomOnlyCancelPending;
    setRoomOnlyCancelPending(false);

    const index = calendarData.findIndex(c => c.id === schedule.id);
    if (index >= 0) {
      const newCalendarData = calendarData.slice();
      newCalendarData.splice(index, 1);
      onCalendarDataChange?.(newCalendarData);
    }

    if (index < 0 || shouldRefetchAfterRoomOnlyCancel) {
      refetchAllSchedule?.();
    }

    onClose();
    setDeletingBookings?.(false);
  };

  useEffect(() => {
    if (inactivateBookingsStatus === "success") handleInactivateSuccess();
  }, [inactivateBookingsStatus]);

  return (
    <>
      {isEditable && !isConferenceServiceSetupLock && (
        <Button
          color={"error"}
          sx={{ padding: "7px 10px 7px 10px" }}
          onClick={handleInactiveBookings}
          data-testid="schedule-item-delete-btn"
        >
          {isMobile ? t("Cancel Booking_short") : t("Cancel Booking")}
        </Button>
      )}
      {!isEditable && schedule.status !== BookingRequestsStatus.REJECTED && (
        <>
          <Button
            color={"error"}
            sx={{ padding: "7px 10px 7px 10px" }}
            onClick={handleRejectBookings}
            data-testid="schedule-item-reject-btn"
          >
            {t("Reject")}
          </Button>
          <Button
            sx={{ padding: "7px 10px 7px 10px" }}
            disabled={schedule.status === BookingRequestsStatus.APPROVED}
            onClick={handleApproveBookings}
            data-testid="schedule-item-approve-btn"
          >
            {t("Approve")}
          </Button>
        </>
      )}

      <ScheduleCancelConferenceVisitorsDialog
        open={cancelVisitorsDialogOpen}
        onClose={handleCancelVisitorsDialogClose}
        onConfirm={handleCancelVisitorsDialogConfirm}
      />
    </>
  );
};
